/**
 * 產品模組 - 型別定義
 * 
 * 命名規範：
 * - Product（產品）：指器材的「型號」，如 Sony FX6、Canon R5C
 * - EquipmentItem（品項）：指實體的「單一設備」，有唯一序號，如 FX6-001、FX6-002
 *   ⚠️ 請使用 equipment-item 模組的 EquipmentItem 型別，不要在此重複定義
 * 
 * 前台使用：Product（產品）- 客戶看到的是產品型號
 * 後台使用：Product（產品）+ EquipmentItem（品項）- 管理員需要管理實體設備
 * 訂單使用：OrderItem 關聯 Product，分配時關聯 EquipmentItem
 * 
 * 序號系統：
 * - 分類序號：母分類 C0001、子分類 S0001
 * - 品牌序號：B0001、B0002...
 * - 配件序號：A0001、A0002...
 * - 產品的 category、subcategory、brand 欄位存放的是序號，不是 ID
 */

// ============================================
// 產品狀態
// ============================================
export type ProductStatus = 'active' | 'inactive' | 'maintenance';

export const PRODUCT_STATUS = {
  ACTIVE: 'active' as const,
  INACTIVE: 'inactive' as const,
  MAINTENANCE: 'maintenance' as const,
};

export const PRODUCT_STATUS_LABELS: Record<ProductStatus, string> = {
  active: '上架中',
  inactive: '已下架',
  maintenance: '維修中',
};

export const PRODUCT_STATUS_COLORS: Record<ProductStatus, { bg: string; text: string; bgLight: string }> = {
  active: { bg: 'bg-emerald-500', text: 'text-emerald-600', bgLight: 'bg-emerald-50' },
  inactive: { bg: 'bg-gray-500', text: 'text-gray-600', bgLight: 'bg-gray-100' },
  maintenance: { bg: 'bg-amber-500', text: 'text-amber-600', bgLight: 'bg-amber-50' },
};

// ============================================
// 前台顯示標籤
// ============================================
// Canonical machine keys — 共 6 個合法 key
export type DisplayTagId = 'hot' | 'new' | 'incoming' | 'staff_required' | 'crew_only' | 'featured';

export interface DisplayTag {
  id: DisplayTagId;
  /** 實際儲存到資料庫的值（canonical machine key）*/
  value: string;
  /** 顯示的標籤文字（中文）*/
  label: string;
  name_zh: string;
  name_ja: string;
  name_en: string;
  icon: string;
  bgColor: string;
  textColor: string;
  borderColor: string;
}

export const DISPLAY_TAGS: DisplayTag[] = [
  { 
    id: 'hot', 
    value: 'hot',
    label: '熱門',
    name_zh: '熱門', 
    name_ja: '人気',
    name_en: 'Popular',
    icon: 'ri-fire-fill',
    bgColor: 'bg-red-50',
    textColor: 'text-red-600',
    borderColor: 'border-red-300'
  },
  { 
    id: 'new', 
    value: 'new',
    label: '新上架',
    name_zh: '新上架', 
    name_ja: '新着',
    name_en: 'New Arrival',
    icon: 'ri-star-fill',
    bgColor: 'bg-green-50',
    textColor: 'text-green-600',
    borderColor: 'border-green-300'
  },
  { 
    id: 'incoming', 
    value: 'incoming',
    label: '預計進貨',
    name_zh: '預計進貨', 
    name_ja: '入荷予定',
    name_en: 'Coming Soon',
    icon: 'ri-time-fill',
    bgColor: 'bg-orange-50',
    textColor: 'text-orange-600',
    borderColor: 'border-orange-300'
  },
  { 
    id: 'staff_required', 
    value: 'staff_required',
    label: '需搭人員',
    name_zh: '需搭人員', 
    name_ja: 'スタッフ操作必須',
    name_en: 'Staff Required',
    icon: 'ri-user-star-fill',
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-600',
    borderColor: 'border-amber-300'
  },
  { 
    id: 'crew_only', 
    value: 'crew_only',
    label: '出班組限定',
    name_zh: '出班組限定', 
    name_ja: 'クルー限定',
    name_en: 'Crew Only',
    icon: 'ri-team-fill',
    bgColor: 'bg-rose-50',
    textColor: 'text-rose-600',
    borderColor: 'border-rose-300'
  },
  {
    id: 'featured',
    value: 'featured',
    label: '精選',
    name_zh: '精選',
    name_ja: 'おすすめ',
    name_en: 'Featured',
    icon: 'ri-award-fill',
    bgColor: 'bg-yellow-50',
    textColor: 'text-yellow-600',
    borderColor: 'border-yellow-300'
  },
];

// ============================================
// 產品介面（對應 products 表）
// ============================================
export interface Product {
  id: string;
  /** 中文名稱 */
  name_zh: string;
  /** 日文名稱 */
  name_ja?: string | null;
  /** 英文名稱 */
  name_en?: string | null;
  /** 產品代碼 */
  code?: string | null;
  /** 分類序號（C0001, C0002...），對應 equipment_categories.code */
  category: string;
  /** 子分類序號（S0001, S0002...），對應 equipment_categories.code */
  subcategory?: string | null;
  /** 品牌序號（B0001, B0002...），對應 brands.code */
  brand?: string | null;
  /** 規格（JSONB） */
  specs?: Record<string, string> | null;
  /** 中文說明（前台顯示） */
  description_zh?: string | null;
  /** 日文說明 */
  description_ja?: string | null;
  /** 每日租金 */
  daily_price: number;
  /** 週租金 */
  weekly_price?: number | null;
  /** 押金 */
  deposit: number;
  /** 成本 */
  cost?: number | null;
  /** 庫存總數 */
  stock_total?: number | null;
  /** 圖片網址陣列 */
  image_urls?: string[] | null;
  /** 產品狀態 */
  status: ProductStatus;
  /** 前台顯示標籤 */
  display_tags?: DisplayTagId[] | null;
  /** 關聯標籤（用於推薦關聯商品） */
  related_tags?: string | null;
  /** 配件清單 */
  accessories?: string[] | null;
  /** 顯示順序 */
  display_order: number;
  /** 購入日期 */
  purchase_date?: string | null;
  /** 瀏覽次數 */
  view_count: number;
  /** 是否精選 */
  is_featured?: boolean;
  /** 建立時間 */
  created_at?: string;
  /** 更新時間 */
  updated_at?: string;
  /** 是否為假資料 */
  isMockData?: boolean;
}

// ============================================
// 品項統計（用於產品卡片顯示）
// ============================================
export interface ItemStats {
  /** 總數 */
  total: number;
  /** 在庫數量 */
  available: number;
  /** 出租中數量 */
  rented: number;
  /** 維修中數量 */
  maintenance?: number;
}

// ============================================
// 產品分類介面（對應 equipment_categories 表）
// ============================================
export interface ProductCategory {
  id: string;
  /** 中文名稱 */
  name_zh: string;
  /** 日文名稱 */
  name_ja?: string | null;
  /** 父分類 ID */
  parent_id?: string | null;
  /** 圖示 */
  icon?: string | null;
  /** 圖示網址 */
  icon_url?: string | null;
  /** 顯示順序 */
  display_order: number;
  /** 是否啟用 */
  is_active: boolean;
  /** 建立時間 */
  created_at?: string;
  /** 更新時間 */
  updated_at?: string;
  /** 是否為假資料 */
  isMockData?: boolean;
}

// ============================================
// 擴展型別（含關聯資料）
// ============================================

/** 產品含分類資訊 */
export interface ProductWithCategory extends Product {
  category_info?: Pick<ProductCategory, 'name_zh' | 'name_ja' | 'icon'>;
}

/** 產品含品項統計 */
export interface ProductWithStats extends Product {
  item_stats?: ItemStats;
}
